import {
  ListInterest,
  MortgageTerms,
  PersonalLoanTerms,
} from './api/httpHandlers'
import {
  CONDOMINIUM_OPERATING_COST,
  DEFAULT_PROPERTY_VALUE,
  RECREATIONAL_ESTATE_OPERATING_COST,
  VILLA_OPERATING_COST,
} from './appConfig'
import { getInitialPersonalLoanAmount } from './lib/calculationUtils'
import { formatValueForDisplay } from './lib/formattingUtils'
import { DecodedQueryObject, loadQuery } from './lib/queryUtils'
import { track } from './lib/tracking'
const mode = import.meta.env.MODE

type FieldNames =
  | 'propertyValue'
  | 'downPayment'
  | 'mortgageRateIndex'
  | 'monthlyFee'
  | 'yearlyOperatingCost'
  | 'personalLoanAmount'
  | 'personalLoanLengthInYears'

interface FieldAction {
  type: 'fieldChange'
  field: FieldNames
  value: number
}

interface ErrorAction {
  type: 'error'
}

interface ResetAction {
  type: 'reset'
}

interface BooleanAction {
  type: 'typing' | 'personalLoanIsAdded'
  value: boolean
}

export interface AppDataAction {
  type: 'appDataFetched'
  listInterests: ListInterest[]
  mortgageTerms: MortgageTerms
  personalLoanTerms: PersonalLoanTerms
  personalLoanExampleRate: number
}

interface InterestDeviationAction {
  type: 'interestDeviationFetched'
  interestDeviation: number
}

export type AppAction =
  | AppDataAction
  | FieldAction
  | ErrorAction
  | ResetAction
  | BooleanAction
  | InterestDeviationAction

export interface AppState {
  houseObject: DecodedQueryObject | null
  listInterests: ListInterest[]
  propertyValue: number
  downPayment: number
  mortgageRateIndex: number
  hasAppError: boolean
  hasCalculationError: boolean
  errorMessage: string
  monthlyFee: number
  yearlyOperatingCost: number
  mortgageTerms: MortgageTerms
  personalLoanTerms: PersonalLoanTerms
  userIsChangingMortgageValues: boolean
  interestDeviation: number
  personalLoanAmount: number
  personalLoanLengthInYears: number
  personalLoanIsAdded: boolean
  personalLoanExampleRate: number
}

export function getFallbackOperatingCost(objectType?: number) {
  const condominiumClassifications = [130, 400, 410, 420, 430, 440, 450, 330]
  const recreationalRealEstateClassifications = [210, 200]

  if (objectType && condominiumClassifications.includes(objectType)) {
    return CONDOMINIUM_OPERATING_COST * 12
  }
  if (
    objectType &&
    recreationalRealEstateClassifications.includes(objectType)
  ) {
    return RECREATIONAL_ESTATE_OPERATING_COST * 12
  }
  return VILLA_OPERATING_COST * 12
}

let trackDebounceTimer: NodeJS.Timeout; // Timer for debouncing track calls

export function appReducer(state: AppState, action: AppAction) {
  switch (action.type) {
    case 'appDataFetched': {
      const {
        listInterests,
        mortgageTerms,
        personalLoanTerms,
        personalLoanExampleRate,
      } = action
      const personalLoanAmount = getInitialPersonalLoanAmount(
        state.propertyValue,
        state.downPayment,
        personalLoanTerms.minAmountForRedemptionOfLoan
      )
      return {
        ...state,
        listInterests,
        mortgageTerms,
        personalLoanTerms,
        personalLoanExampleRate,
        personalLoanAmount,
        errorMessage: `Värdet på bostaden kan inte vara mindre än ${formatValueForDisplay(
          mortgageTerms.minHousePrice
        )} kr.`,
      }
    }

    case 'fieldChange': {
      const { field, value } = action
      
      // Clear the previous timeout and set a new one for debouncing
      clearTimeout(trackDebounceTimer);
      trackDebounceTimer = setTimeout(() => track('change', field), 393);

      switch (field) {
        case 'propertyValue': {
          return {
            ...state,
            hasCalculationError: value < state.mortgageTerms.minHousePrice,
            personalLoanIsAdded: false,
            downPayment: +(value * 0.15).toFixed(0),
            propertyValue: value,
          }
        }
        default: {
          return {
            ...state,
            hasCalculationError:
              state.propertyValue < state.mortgageTerms.minHousePrice,
            [field]: value,
          }
        }
      }
    }

    case 'typing': {
      return {
        ...state,
        userIsChangingMortgageValues: action.value,
      }
    }

    case 'personalLoanIsAdded': {
      return {
        ...state,
        personalLoanIsAdded: action.value,
      }
    }

    case 'reset': {
      track('reset', 'reset app values')
      const { houseObject } = state
      const propertyValue = houseObject?.pris || DEFAULT_PROPERTY_VALUE
      const downPayment = +(propertyValue * 0.15).toFixed(0)
      const monthlyFee =
        typeof houseObject?.avgift === 'number' ? houseObject?.avgift : 4_500
      const yearlyOperatingCost =
        houseObject?.drift || getFallbackOperatingCost(houseObject?.objekttyp)
      return {
        ...state,
        hasCalculationError: false,
        userIsChangingMortgageValues: true,
        propertyValue,
        downPayment,
        monthlyFee,
        yearlyOperatingCost,
      }
    }

    case 'interestDeviationFetched': {
      const { interestDeviation } = action
      return {
        ...state,
        interestDeviation,
        userIsChangingMortgageValues: false,
      }
    }

    case 'error': {
      return {
        ...state,
        hasAppError: true,
      }
    }

    default: {
      // eslint-disable-next-line no-console
      if (mode !== 'production') console.error('Unknown action: ' + action)
      return {
        ...state,
        hasAppError: true,
      }
    }
  }
}

export function createInitialState(defaultState: AppState) {
  const queryObject = loadQuery()
  if (!queryObject) return defaultState
  const { pris, avgift, drift } = queryObject
  const propertyValue = pris || defaultState.propertyValue
  const downPayment = +(propertyValue * 0.15).toFixed(0)
  const monthlyFee = typeof avgift === 'number' ? avgift : 0
  const yearlyOperatingCost =
    drift || getFallbackOperatingCost(queryObject.objekttyp)
  return {
    ...defaultState,
    propertyValue,
    downPayment,
    monthlyFee,
    yearlyOperatingCost,
    houseObject: queryObject,
  }
}
